/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2022, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { createSelector } from 'reselect';

import { ApiStatus } from '../../../../ts';
import { Release } from '../../../../ts/release';
import { RootState } from '../../../../ts/state';
import { selectTargetEnvironmentId } from '../environments/selectors';
import { selectAllReleases } from './core-selectors';

export { selectAllReleases, selectReleaseById } from './core-selectors';

export const areReleasesLoaded = (state: RootState): boolean =>
  state.releases?.listStatus === ApiStatus.SUCCEEDED;

export const areReleasesLoading = (state: RootState): boolean =>
  state.releases?.listStatus === ApiStatus.LOADING;

/**
 * Fetches the selectAllReleases array then reorders it so that Live (if present)
 * is moved to the first element, and all remaining releases are in descending
 * release number order.
 */
export const selectOrderedAllReleases = createSelector(
  [selectAllReleases, selectTargetEnvironmentId],
  (releases, liveId): Release[] => {
    //shallow clone, just reordering
    // first sort to general descending order
    releases.sort((a, b) => parseInt(b.release) - parseInt(a.release));

    const liveIndex = releases.findIndex(rel =>
      rel.linked_assistant_references?.some(
        ref => ref.assistant_id === liveId || ref.environment_id === liveId
      )
    );
    if (liveIndex > 0) {
      releases.splice(0, 0, releases.splice(liveIndex, 1)[0]);
    }
    return releases;
  }
);

export const selectIsDeploying = (state: RootState): boolean =>
  state.releases?.deployStatus === ApiStatus.LOADING;

export const selectIsCreating = (state: RootState): boolean =>
  state.releases?.createStatus === ApiStatus.LOADING;
